import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDrag } from 'react-dnd';
import Icon from '../Icon';
import { StringNumber } from '../../types';
import DisplaySetMessageListTooltip from '../DisplaySetMessageListTooltip';

/**
 * Display a thumbnail for a display set.
 */
const Thumbnail = ({
  displaySetInstanceUID,
  className,
  imageSrc,
  imageAltText,
  description,
  seriesNumber,
  numInstances,
  countIcon,
  messages,
  dragData,
  isActive,
  onClick,
  onDoubleClick,
}): React.ReactNode => {
  // TODO: We should wrap our thumbnail to create a "DraggableThumbnail", as
  // this will still allow for "drag", even if there is no drop target for the
  // specified item.
  const [, drag] = useDrag({
    type: 'displayset',
    item: { ...dragData },
    canDrag: function (monitor) {
      return Object.keys(dragData).length !== 0;
    },
  });

  // Check if this is an enhanced image based on description
  const isEnhanced =
    description &&
    (description.toLowerCase().includes('enhanced') ||
      description.toLowerCase().includes('focus') ||
      description.toLowerCase().includes('filter'));

  // Truncate description for enhanced images to make them cleaner
  let displayDescription = description;
  if (isEnhanced && description) {
    // Use regex to match patterns like "X - Enhanced from Y" and convert to "X - Enhanced"
    // This handles multiple possible formats
    const enhancedMatch = description.match(/(.*?\s*-\s*Enhanced)(?:\s+from\s+.*)?/i);
    if (enhancedMatch && enhancedMatch[1]) {
      displayDescription = enhancedMatch[1];
    }
    // Prefix with a corner arrow so it visually reads as nested under its original.
    displayDescription = `↳ ${displayDescription}`;
  }

  return (
    <div
      className={classnames(
        className,
        'group mb-8 flex flex-1 cursor-pointer select-none flex-col px-3 outline-none'
      )}
      id={`thumbnail-${displaySetInstanceUID}`}
      data-cy={`study-browser-thumbnail`}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      role="button"
      tabIndex={0}
    >
      <div ref={drag}>
        <div
          className={classnames(
            'min-h-32 relative flex flex-1 items-center justify-center overflow-hidden rounded-md bg-black text-base text-white',
            isActive
              ? 'border-primary-light border-2'
              : 'border-secondary-light hover:border-primary-active border'
          )}
          style={{
            margin: isActive ? '0' : '1px',
          }}
        >
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={imageAltText}
              className="min-h-32 object-none"
              crossOrigin="anonymous"
            />
          ) : (
            <div>{imageAltText}</div>
          )}

          {/* Enhanced badge */}
          {isEnhanced && (
            <div
              className="bg-primary-light absolute top-0 right-0 rounded-bl-md px-2 py-1 text-xs font-bold text-black"
              style={{
                backgroundColor: '#4285f4',
                color: 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                zIndex: 1,
              }}
            >
              ENHANCED
            </div>
          )}
        </div>
        <div className="text-primary-light  flex flex-1 flex-row items-center pt-2 text-base">
          <div className="mr-4">
            <span className="text-primary-light font-bold">{'S: '}</span>
            {seriesNumber}
          </div>
          <div className="flex flex-1 flex-row items-center">
            <Icon
              name={countIcon || 'group-layers'}
              className="mr-2 w-3"
            />
            {` ${numInstances}`}
          </div>
          <DisplaySetMessageListTooltip
            messages={messages}
            id={`display-set-tooltip-${displaySetInstanceUID}`}
          />
        </div>
        <div className="break-all text-base text-white">
          {displayDescription}
          {isEnhanced && (
            <span
              className="ml-2 inline-block"
              style={{
                color: '#4285f4',
                fontWeight: 'bold',
              }}
            >
              ✓
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

Thumbnail.propTypes = {
  displaySetInstanceUID: PropTypes.string.isRequired,
  className: PropTypes.string,
  imageSrc: PropTypes.string,
  /**
   * Data the thumbnail should expose to a receiving drop target. Use a matching
   * `dragData.type` to identify which targets can receive this draggable item.
   * If this is not set, drag-n-drop will be disabled for this thumbnail.
   *
   * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
   */
  dragData: PropTypes.shape({
    /** Must match the "type" a dropTarget expects */
    type: PropTypes.string.isRequired,
  }),
  imageAltText: PropTypes.string,
  description: PropTypes.string.isRequired,
  seriesNumber: StringNumber.isRequired,
  numInstances: PropTypes.number.isRequired,
  countIcon: PropTypes.string,
  messages: PropTypes.object,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
};

Thumbnail.defaultProps = {
  dragData: {},
};

export default Thumbnail;
